import type { FormEvent } from 'react';
import { useState } from 'react';
import type { Nullable } from 'corgi-types';
import { ContactFormCommunicationChannel } from 'graphql-ts-types';
import type { CustomerRequest, GQLLocale } from 'graphql-ts-types';

import { executeQuery } from 'common/gql';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FormData } from 'common/types/form';
import type { DispatchState } from 'common/types/React';
import { getQueryParamsString } from 'common/utils/url';
import { FieldName } from '../components/enums';
import type { ContactFormFieldSlice, Settings } from '../components/types';
import submitFormMutation from '../gql/mutation/submitForm.gql';
import { getChannelSelectionAvailability } from '../utils/channel';
import { hasLineNo, LINE_NO_SEPARATOR } from '../utils/products';
import { clearStorage } from '../utils/storage';
import { formSubmitTrackingEvent } from '../utils/tracking';
import { valueToImages } from './images';

interface SubmitFormMutationVariables {
  request: CustomerRequest;
}

interface GetContactFormRequest {
  formData: FormData<FieldName>;
  reason: string;
  locale: GQLLocale;
  customerNumber: Nullable<string>;
}

export interface UseSubmitForm {
  reason: string;
  settings: Settings;
  isFormInvalid: boolean;
  formData: FormData<FieldName>;
  setError: DispatchState<boolean>;
  customerNumber: Nullable<string>;
  slices: ContactFormFieldSlice[];
  isChannelSelectionActive: boolean;
  setCallbackNotAvailableError: (error: boolean) => void;
  selectedChannel: Nullable<ContactFormCommunicationChannel>;
  sellerName: Nullable<string>;
  orderId: Nullable<string>;
}

const getContactFormRequest = ({
  formData,
  reason,
  locale,
  customerNumber,
}: GetContactFormRequest): CustomerRequest => {
  const firstName = formData.get(FieldName.FirstName)?.trim();
  const lastName = formData.get(FieldName.LastName)?.trim();
  const productName = formData.get(FieldName.ProductName) ?? null;
  const product = hasLineNo(productName)
    ? { lineNumbers: productName?.split(LINE_NO_SEPARATOR) ?? [] }
    : { sku: productName, lineNumbers: [] };
  const channel = formData.get(FieldName.Channel) as ContactFormCommunicationChannel;

  return {
    firstName,
    lastName,
    locale,
    reasonName: reason,
    phone: formData.get(FieldName.Phone) ?? null,
    email: formData.get(FieldName.EmailAddress) ?? null,
    orderNumber: formData.get(FieldName.OrderNumber) ?? null,
    comment: formData.get(FieldName.Comment) ?? null,
    urls: valueToImages(formData.get(FieldName.Images)) ?? null,
    customerNumber,
    channel,
    ...product,
  };
};

const getIsEmailOptional = (slices: ContactFormFieldSlice[]) => {
  const emailSlice = slices.find(slice => slice.fieldName === FieldName.EmailAddress);

  return emailSlice?.required === false;
};

const buildSuccessUrl = ({
  baseUrl,
  channel,
  slices,
  sellerName,
  orderId,
}: {
  baseUrl: string;
  channel: string | undefined;
  slices: ContactFormFieldSlice[];
  sellerName: Nullable<string>;
  orderId: Nullable<string>;
}) => {
  const params: Record<string, string> = {};

  if (channel) {
    params.channel = channel;
  } else if (getIsEmailOptional(slices)) {
    params.isEmailOptional = 'true';
  }

  if (sellerName && orderId) {
    params.sellerName = sellerName;
    params.orderId = orderId;
  }

  const queryString = getQueryParamsString(params);

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

export const useSubmitForm = ({
  reason,
  slices,
  setError,
  formData,
  settings,
  isFormInvalid,
  customerNumber,
  selectedChannel,
  isChannelSelectionActive,
  setCallbackNotAvailableError,
  sellerName,
  orderId,
}: UseSubmitForm) => {
  const { t, p } = useLocalization();
  const locale = useGQLLocale();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const makeRequest = async () => {
    setError(false);
    setIsLoading(true);

    try {
      const request = getContactFormRequest({
        formData,
        reason,
        customerNumber,
        locale,
      });

      await executeQuery<string, SubmitFormMutationVariables>({
        query: submitFormMutation,
        variables: { request },
        useApq: true,
      });

      clearStorage();
      setIsSuccess(true);

      const successPageUrl = p.t('h24_contact_form_success_page');
      const channel = formData.get(FieldName.Channel);
      location.href = buildSuccessUrl({
        baseUrl: successPageUrl,
        channel,
        slices,
        sellerName,
        orderId,
      });
    } catch {
      setError(true);
    }

    setIsLoading(false);
  };

  const submitForm = (e: FormEvent) => {
    e.preventDefault();

    const shouldCheckCallbackAvailability =
      selectedChannel !== ContactFormCommunicationChannel.Email &&
      isChannelSelectionActive &&
      !getChannelSelectionAvailability(slices, settings);

    if (shouldCheckCallbackAvailability) {
      setCallbackNotAvailableError(true);

      return;
    }

    if (!isFormInvalid && !isLoading) {
      makeRequest();
      formSubmitTrackingEvent(reason, t('h24_contact_form_submit'));
    }
  };

  return { submitForm, isLoading, isSuccess };
};
